import { convertDateToLocaleString } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import {
  EditorV3,
  EditorV3DropListItem,
  IEditorV3,
  cleanIsEqual,
  getTextFromEditorV3,
  stringToV3,
} from "editor-v3";
import { isEqual } from "lodash";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useLocation } from "react-router-dom";
import { searchUsers } from "../../../functions/api/user/searchUsers";
import "./Comments.css";
import { CreateCommentButton } from "./CreateCommentButton";
import { DeleteCommentButton } from "./DeleteCommentButton";
import { CommentViewContext } from "./interface";
import { isCommentVisible } from "./isCommentVisible";
import { isRedirectingFromNotifications } from "./isRedirectingFromNotifications";
import { ResolveCommentButton } from "./ResolveCommentButton";
import { UpdateCommentButton } from "./UpdateCommentButton";
import { useComments } from "./useComments";

/**
 * Props for the CommentBox component.
 *
 * @param activeContext - The context in which the comment is viewed or edited
 * @param commentId - The ID of the comment to display or edit
 * @param disabled - Optional. When true, disables editing and interaction
 */
interface CommentBoxProps {
  activeContext: CommentViewContext;
  commentId: string;
  disabled?: boolean;
}

/**
 * Status of the comment save button
 * Available statuses:
 * - "new": The comment is new and has not been saved yet.
 * - "editing": The comment is being edited.
 * - "saved": The comment has been saved successfully.
 * - "error": There was an error saving the comment.
 * - "disabled": The comment box is disabled and cannot be interacted with.
 */
export type CommentSaveButtonStatus = "new" | "editing" | "saved" | "error" | "disabled";

/**
 * Comment box component for displaying and editing a single comment.
 *
 * @param props - Component props.
 * @param props.activeContext - The context in which the comment is viewed or edited.
 * @param props.commentId - The ID of the comment to display or edit.
 * @param props.disabled - Optional. When true, disables editing and interaction.
 */
export const CommentBox = ({ activeContext, commentId, disabled }: CommentBoxProps): ReactNode => {
  const [_isPending, startTransition] = useTransition();
  // Get contexts
  const appSettings = useContext(AppSettingsContext);
  const { commentPanelStatus, readComment, setCommentPanelStatus, commentFilter } =
    useComments(activeContext);
  const location = useLocation();

  // Ref to the comment box element for managing focus
  const commentBoxRef = useRef<HTMLDivElement>(null);

  /**
   * Reads the comment with the given ID from the context
   */
  const thisComment = readComment(commentId);
  /**
   * Initializes the original text of the comment
   */
  const originalText = thisComment?.content ?? stringToV3("");

  /**
   * Determines if the current user can edit the comment
   */
  const canEdit = useMemo(
    () =>
      thisComment?.authorId === appSettings.userId &&
      !disabled &&
      thisComment.commentReviewStatus !== "Resolved",
    [appSettings.userId, disabled, thisComment?.authorId, thisComment?.commentReviewStatus],
  );

  /**
   * State to manage the current content of the comment editor
   */
  const [currentContent, setCurrentContent] = useState<IEditorV3>(originalText);
  /**
   * State to manage the current status of the comment save button
   */
  const [currentStatus, setCurrentStatus] = useState<CommentSaveButtonStatus>(
    !canEdit ? "disabled" : thisComment?.id.startsWith("new-") ? "new" : "saved",
  );

  /**
   * Update currentStatus if canEdit changes, shouldn't be possible to have new comments with a resolved status
   */
  useEffect(() => {
    if (canEdit && currentStatus === "disabled" && !thisComment?.id.startsWith("new-")) {
      startTransition(() => setCurrentStatus("saved"));
    }
  }, [canEdit, currentStatus, thisComment?.id]);

  /**
   * Effect to reset the current content when the comment is updated from the top down and is not in editing or new status
   */
  useEffect(() => {
    if (!isEqual(currentContent, originalText) && !["editing", "new"].includes(currentStatus)) {
      startTransition(() => setCurrentContent(originalText));
    }
  }, [currentContent, currentStatus, originalText]);

  /**
   * Effect to set activeRelatedTo when the comment is focused from notifications
   */
  useEffect(() => {
    if (
      thisComment?.relatedTo &&
      commentPanelStatus?.highlightCommentId === commentId &&
      thisComment?.relatedTo !== commentPanelStatus.activeRelatedTo
    ) {
      setCommentPanelStatus({
        ...commentPanelStatus,
        activeRelatedTo: thisComment.relatedTo,
      });
    }
  }, [thisComment?.relatedTo, commentId, commentPanelStatus, setCommentPanelStatus]);

  /**
   * Determines if the user can post this comment based on the current status and content
   */
  const canPost = useMemo(
    () =>
      canEdit &&
      (currentStatus === "new" || currentStatus === "editing") &&
      getTextFromEditorV3(currentContent).join("").trim() !== "" &&
      !cleanIsEqual(currentContent, originalText),
    [canEdit, currentContent, currentStatus, originalText],
  );

  /**
   * Function to handle the @ mention list for the editor
   * @param typedString - The string currently being typed in the editor
   * @returns A promise that resolves to a list of users matching the typed string
   */
  const atListFunction = useCallback(
    async (typedString: string) => {
      if (typedString.length === 1)
        return [
          {
            text: "Waiting...",
            data: {},
          },
        ];
      const request = await searchUsers(appSettings.backEndUrl, typedString.slice(1));
      const ret: EditorV3DropListItem<{ prid: string }>[] = [];
      /* istanbul ignore else */
      if (request?.success) {
        request.data
          ?.sort((a, b) => `${a.displayName}`.localeCompare(`${b.displayName}`))
          .forEach((u) => {
            /* istanbul ignore else */
            if (u.displayName && u.prid)
              ret.push({
                text: u.displayName,
                data: {
                  prid: u.prid,
                },
              });
          });
      }
      return ret;
    },
    [appSettings.backEndUrl],
  );

  /**
   * Function to update the comment panel status
   * @param updates - Updates to the comment panel status
   */
  const updateCommentPanelStatus = useCallback(
    (updates: Partial<typeof commentPanelStatus>) => {
      /* istanbul ignore else */
      if (setCommentPanelStatus && commentPanelStatus) {
        setCommentPanelStatus({
          ...commentPanelStatus,
          ...updates,
        });
      }
    },
    [setCommentPanelStatus, commentPanelStatus],
  );

  /**
   * Determines if the comment should be visible based on the filter and review status.
   */
  const shouldShowComment = useMemo(
    () => isCommentVisible(commentFilter, thisComment?.commentReviewStatus),
    [commentFilter, thisComment?.commentReviewStatus],
  );

  /**
   * Memo for the class name for the comment based on its status
   */
  const getCommentClassName = useMemo(() => {
    if (
      commentPanelStatus?.highlightCommentId === commentId ||
      (commentPanelStatus?.activeRelatedTo &&
        commentPanelStatus?.activeRelatedTo === thisComment?.relatedTo)
    ) {
      return "highlight";
    }

    return "";
  }, [commentId, commentPanelStatus, thisComment]);

  // Add global mousedown handler to clear highlight when clicking outside comment box
  const handleDocumentMouseDown = useCallback(
    (e: MouseEvent) => {
      if (
        e.target &&
        commentBoxRef.current &&
        !commentBoxRef.current.contains(e.target as Node) &&
        (commentPanelStatus?.activeRelatedTo !== "" ||
          commentPanelStatus?.highlightCommentId !== "")
      ) {
        updateCommentPanelStatus({
          highlightCommentId: "",
          activeRelatedTo: "",
        });
      }
    },
    [
      commentPanelStatus?.activeRelatedTo,
      commentPanelStatus?.highlightCommentId,
      updateCommentPanelStatus,
    ],
  );

  useEffect(() => {
    if (
      commentPanelStatus?.highlightCommentId === commentId &&
      !isRedirectingFromNotifications(location.search)
    ) {
      document.addEventListener("mousedown", handleDocumentMouseDown);

      return () => {
        document.removeEventListener("mousedown", handleDocumentMouseDown);
      };
    }
  }, [handleDocumentMouseDown, commentId, commentPanelStatus?.highlightCommentId, location.search]);

  /**
   * Shared onBlur handler for comment box and editor
   */
  const handleBlur = useCallback(
    (
      e:
        | React.FocusEvent<HTMLDivElement>
        | { relatedTarget: EventTarget | null; currentTarget?: EventTarget | null },
    ) => {
      const commentBox = commentBoxRef.current;
      const nextFocused = e.relatedTarget as Node | null;
      // Only handle blur if this is the outermost comment box
      if (
        isRedirectingFromNotifications(location.search) ||
        ("currentTarget" in e && e.currentTarget && commentBox && e.currentTarget !== commentBox)
      ) {
        return;
      }
      if (!nextFocused || !commentBox?.contains(nextFocused)) {
        updateCommentPanelStatus({
          highlightCommentId: "",
          activeRelatedTo: "",
        });
      }
    },
    [updateCommentPanelStatus, location.search],
  );

  return !thisComment || !shouldShowComment ? null : (
    <div className={`comment ${getCommentClassName}`}>
      <div className="comment-text-holder position-relative">
        <div
          className="comment-box"
          role="region"
          id={`comment-${commentId}-box`}
          tabIndex={0}
          ref={commentBoxRef}
          onBlur={handleBlur}
          onFocus={() => {
            if (
              commentPanelStatus &&
              (thisComment.relatedTo ?? "") !== commentPanelStatus.activeRelatedTo
            ) {
              updateCommentPanelStatus({
                highlightCommentId: thisComment.id,
                activeRelatedTo: thisComment.relatedTo,
              });
            }
          }}
        >
          <div className="row">
            <div className="comment-header col pe-0">
              <div className="comment-header-name">{thisComment.authorDisplayName}</div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <EditorV3
                className="comment-editor"
                id={`comment-${commentId}`}
                input={currentContent}
                allowNewLine
                atListFunction={atListFunction}
                debounceMilliseconds={-1}
                aria-label="Comment text"
                aria-readonly={["editing", "new"].includes(currentStatus)}
                editable={["editing", "new"].includes(currentStatus)}
                setObject={(ret) => {
                  setCurrentContent({
                    lines: ret.lines,
                  });
                }}
                resize={!disabled ? "vertical" : undefined}
                onBlur={handleBlur}
              />
            </div>
            {
              <>
                <div className="comment-button-holder d-flex flex-column gap-1 col pt-1">
                  {!disabled && (
                    <div className="d-flex flex-row gap-1">
                      <UpdateCommentButton
                        activeContext={activeContext}
                        currentStatus={currentStatus}
                        disabled={["new", "editing"].includes(currentStatus) ? !canPost : !canEdit}
                        commentId={commentId}
                        currentContent={currentContent}
                        setCurrentStatus={setCurrentStatus}
                      />
                      <DeleteCommentButton
                        activeContext={activeContext}
                        commentId={commentId}
                        canEdit={canEdit}
                      />
                    </div>
                  )}

                  {activeContext !== "Toc" && (
                    <>
                      <ResolveCommentButton
                        activeContext={activeContext}
                        commentId={commentId}
                        disabled={["new", "editing", "error"].includes(currentStatus)}
                      />

                      <CreateCommentButton
                        id={`reply-comment-${commentId}`}
                        activeContext={activeContext}
                        parentId={commentId}
                        relatedTo={thisComment.relatedTo}
                        reply
                        disabled={
                          ["new", "editing", "error"].includes(currentStatus) ||
                          thisComment.commentReviewStatus === "Resolved"
                        }
                      />
                    </>
                  )}
                </div>
              </>
            }
            <div className="row w-100 pe-0">
              <div className="comment-header comment-header-time col pe-0 text-muted">
                {thisComment.entityVersion && (
                  <div className="comment-header-version">
                    <em>Added on version {thisComment.entityVersion}</em>
                  </div>
                )}
                <em>
                  {thisComment.updateCount >= 1 && <>Edited </>}
                  {convertDateToLocaleString(thisComment.updatedAt)}
                </em>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CommentBox.displayName = "CommentBox";
